import { supabase } from '../lib/supabase';
import { News, CreateNewsData, NewsStats } from '../types/models/news';

class NewsService {
  /**
   * Create a new news article
   */
  async createNews(data: CreateNewsData, adminId: string, adminName: string): Promise<string> {
    try {
      // Note: author_id is not sent because admin dashboard uses Firebase auth,
      // not Supabase auth. The author_name field tracks who created the news.
      const { data: news, error } = await supabase
        .from('news')
        .insert({
          title: data.title.trim(),
          content: data.content.trim() || null,
          summary: data.summary.trim(),
          cover_image_url: data.coverImageUrl?.trim() || null,
          sport: data.sport,
          selection_level: data.selectionLevel,
          location: data.location.trim(),
          selection_date: data.selectionDate && data.selectionDate.trim() !== ''
            ? new Date(data.selectionDate).toISOString()
            : null,
          end_date: data.endDate && data.endDate.trim() !== ''
            ? new Date(data.endDate).toISOString()
            : null,
          registration_deadline: data.registrationDeadline && data.registrationDeadline.trim() !== ''
            ? new Date(data.registrationDeadline).toISOString()
            : null,
          eligibility_criteria: data.eligibilityCriteria?.trim() || null,
          registration_url: data.registrationUrl?.trim() || null,
          contact_info: data.contactInfo?.trim() || null,
          organizing_body: data.organizingBody?.trim() || null,
          age_group: data.ageGroup?.trim() || null,
          gender: data.gender || 'both',
          max_participants: data.maxParticipants || null,
          winner: data.winner?.trim() || null,
          author_name: adminName,
          is_pinned: data.isPinned || false,
          priority: data.priority || 'normal',
        })
        .select('id')
        .single();

      if (error) {
        console.error('Supabase error details:', error.code, error.message, error.details, error.hint);
        throw error;
      }
      return news.id;
    } catch (error) {
      console.error('Error creating news:', error);
      throw new Error('Failed to create news');
    }
  }

  /**
   * Get all news articles (for admin dashboard)
   */
  async getAllNews(): Promise<News[]> {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching news:', error);
      throw new Error('Failed to fetch news');
    }
  }

  /**
   * Get active news (for public display)
   */
  async getActiveNews(): Promise<News[]> {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('is_active', true)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching active news:', error);
      throw new Error('Failed to fetch active news');
    }
  }

  /**
   * Get a single news article by ID
   */
  async getNewsById(newsId: string): Promise<News | null> {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('id', newsId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return this.mapToModel(data);
    } catch (error) {
      console.error('Error fetching news by ID:', error);
      throw new Error('Failed to fetch news');
    }
  }

  /**
   * Update an existing news article
   */
  async updateNews(newsId: string, updates: Partial<CreateNewsData>): Promise<void> {
    try {
      const dbUpdates: Record<string, any> = {};

      if (updates.title !== undefined) dbUpdates.title = updates.title.trim();
      if (updates.content !== undefined) dbUpdates.content = updates.content.trim() || null;
      if (updates.summary !== undefined) dbUpdates.summary = updates.summary.trim();
      if (updates.coverImageUrl !== undefined) dbUpdates.cover_image_url = updates.coverImageUrl?.trim() || null;
      if (updates.sport !== undefined) dbUpdates.sport = updates.sport;
      if (updates.selectionLevel !== undefined) dbUpdates.selection_level = updates.selectionLevel;
      if (updates.location !== undefined) dbUpdates.location = updates.location.trim();
      if (updates.selectionDate !== undefined) {
        dbUpdates.selection_date = updates.selectionDate && updates.selectionDate.trim() !== ''
          ? new Date(updates.selectionDate).toISOString()
          : null;
      }
      if (updates.endDate !== undefined) {
        dbUpdates.end_date = updates.endDate && updates.endDate.trim() !== ''
          ? new Date(updates.endDate).toISOString()
          : null;
      }
      if (updates.registrationDeadline !== undefined) {
        dbUpdates.registration_deadline = updates.registrationDeadline && updates.registrationDeadline.trim() !== ''
          ? new Date(updates.registrationDeadline).toISOString()
          : null;
      }
      if (updates.eligibilityCriteria !== undefined) dbUpdates.eligibility_criteria = updates.eligibilityCriteria?.trim() || null;
      if (updates.registrationUrl !== undefined) dbUpdates.registration_url = updates.registrationUrl?.trim() || null;
      if (updates.contactInfo !== undefined) dbUpdates.contact_info = updates.contactInfo?.trim() || null;
      if (updates.organizingBody !== undefined) dbUpdates.organizing_body = updates.organizingBody?.trim() || null;
      if (updates.ageGroup !== undefined) dbUpdates.age_group = updates.ageGroup?.trim() || null;
      if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
      if (updates.maxParticipants !== undefined) dbUpdates.max_participants = updates.maxParticipants || null;
      if (updates.winner !== undefined) dbUpdates.winner = updates.winner?.trim() || null;
      if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;

      const { error } = await supabase
        .from('news')
        .update(dbUpdates)
        .eq('id', newsId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating news:', error);
      throw new Error('Failed to update news');
    }
  }

  /**
   * Delete a news article
   */
  async deleteNews(newsId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('news')
        .delete()
        .eq('id', newsId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting news:', error);
      throw new Error('Failed to delete news');
    }
  }

  /**
   * Toggle active status
   */
  async toggleActive(newsId: string, isActive: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('news')
        .update({ is_active: isActive })
        .eq('id', newsId);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling news status:', error);
      throw new Error('Failed to update news status');
    }
  }

  /**
   * Declare winner for a news/selection event
   */
  async declareWinner(newsId: string, winner: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('news')
        .update({ winner: winner.trim() || null })
        .eq('id', newsId);

      if (error) throw error;
    } catch (error) {
      console.error('Error declaring winner:', error);
      throw new Error('Failed to declare winner');
    }
  }

  /**
   * Toggle pinned status
   */
  async togglePinned(newsId: string, isPinned: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('news')
        .update({ is_pinned: isPinned })
        .eq('id', newsId);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling pin status:', error);
      throw new Error('Failed to update pin status');
    }
  }

  /**
   * Get news statistics
   */
  async getStats(): Promise<NewsStats> {
    try {
      const news = await this.getAllNews();
      const now = new Date();
      const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const stats: NewsStats = {
        total: news.length,
        active: 0,
        pinned: 0,
        upcomingSelections: 0,
      };

      news.forEach((n) => {
        if (n.isActive) stats.active++;
        if (n.isPinned) stats.pinned++;
        if (n.selectionDate) {
          const selectionDate = new Date(n.selectionDate);
          if (selectionDate >= now && selectionDate <= thirtyDaysLater) {
            stats.upcomingSelections++;
          }
        }
      });

      return stats;
    } catch (error) {
      console.error('Error calculating stats:', error);
      throw new Error('Failed to calculate stats');
    }
  }

  /**
   * Map database row to News model
   */
  private mapToModel(data: any): News {
    return {
      id: data.id,
      title: data.title,
      content: data.content,
      summary: data.summary,
      coverImageUrl: data.cover_image_url,
      sport: data.sport,
      selectionLevel: data.selection_level,
      location: data.location,
      selectionDate: data.selection_date,
      endDate: data.end_date,
      registrationDeadline: data.registration_deadline,
      eligibilityCriteria: data.eligibility_criteria,
      registrationUrl: data.registration_url,
      contactInfo: data.contact_info,
      organizingBody: data.organizing_body,
      ageGroup: data.age_group,
      gender: data.gender || 'both',
      maxParticipants: data.max_participants,
      winner: data.winner,
      authorId: data.author_id,
      authorName: data.author_name || 'Admin',
      isActive: data.is_active,
      isPinned: data.is_pinned,
      views: data.views || 0,
      priority: data.priority || 'normal',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const newsService = new NewsService();
export default newsService;
